import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';
import SegmentationObjectValue from 'lumen/utils/datamodels/revenue-insight/segmentation-object-value';

const SEGMENTATION_IDS_WITH_OCCUPANCY = [
  RI_BREAKDOWNS.ALL,
  RI_BREAKDOWNS.IMPACT_DATE,
  RI_BREAKDOWNS.SUBSCRIPTION_ID,
];

const SEGMENTATION_IDS_WITH_REVPAR = [
  RI_BREAKDOWNS.ALL,
  RI_BREAKDOWNS.IMPACT_DATE,
  RI_BREAKDOWNS.SUBSCRIPTION_ID,
];

export default class CalculatedValues {
  get BOB() {
    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject.value,
    );
  }

  get budget() {
    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject.budgetValue,
      this.segmentationObject.value?.capacity,
    );
  }

  get budgetChange() {
    return SegmentationObjectValue.calculateChange(this.BOB, this.budget);
  }

  get cancellationBOB() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject.cancellationValue,
      this.segmentationObject.value?.capacity,
    );
  }

  /**
   * Returns `cancellationValueFiltered` if it's present, otherwise returns `cancellationValue`.
   * `cancellationValueFiltered` is present for the Cancellation Leadtime breakdown
   * or in all other breakdowns when a filter on a Cancellation Leadtime breakdown item is active.
   */
  get cancellationBOBOrFiltered() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    const cancellationValueSegmentationObject =
      this.segmentationObject.cancellationValueFiltered ??
      this.segmentationObject.cancellationValue;

    return this.copyAndCalculateOccupancyAndRevpar(
      cancellationValueSegmentationObject,
      this.segmentationObject.value?.capacity,
    );
  }

  get cancellationBOBOrFilteredPercentage() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return SegmentationObjectValue.calculateCancellationPercentage(
      this.BOB,
      this.cancellationBOBOrFiltered,
    );
  }

  get cancellationBOBPercentage() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return SegmentationObjectValue.calculateCancellationPercentage(
      this.BOB,
      this.cancellationBOB,
    );
  }

  get cancellationChange() {
    return SegmentationObjectValue.calculateChange(
      this.cancellationBOB,
      this.cancellationReference,
    );
  }

  get cancellationChangePercentage() {
    return SegmentationObjectValue.calculateChange(
      this.cancellationBOBPercentage,
      this.cancellationReferencePercentage,
    );
  }

  get cancellationOrFilteredChange() {
    return SegmentationObjectValue.calculateChange(
      this.cancellationBOBOrFiltered,
      this.cancellationReferenceOrFiltered,
    );
  }

  get cancellationOrFilteredChangePercentage() {
    return SegmentationObjectValue.calculateChange(
      this.cancellationBOBOrFilteredPercentage,
      this.cancellationReferenceOrFilteredPercentage,
    );
  }

  get cancellationReference() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject.cancellationReferenceValue,
      this.segmentationObject.referenceValue?.capacity,
    );
  }

  get cancellationReferenceOrFiltered() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    const cancellationReferenceSegmentationObject =
      this.segmentationObject.cancellationReferenceValueFiltered ??
      this.segmentationObject.cancellationReferenceValue;

    return this.copyAndCalculateOccupancyAndRevpar(
      cancellationReferenceSegmentationObject,
      this.segmentationObject.referenceValue?.capacity,
    );
  }

  get cancellationReferenceOrFilteredPercentage() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return SegmentationObjectValue.calculateCancellationPercentage(
      this.reference,
      this.cancellationReferenceOrFiltered,
    );
  }

  get cancellationReferencePercentage() {
    if (this.isTargetActive) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return SegmentationObjectValue.calculateCancellationPercentage(
      this.reference,
      this.cancellationReference,
    );
  }

  get change() {
    return SegmentationObjectValue.calculateChange(this.BOB, this.reference);
  }

  get forecast() {
    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject.forecastValue,
      this.segmentationObject.value?.capacity,
    );
  }

  get forecastChange() {
    return SegmentationObjectValue.calculateChange(this.BOB, this.forecast);
  }

  get forecastReference() {
    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject.forecastReference,
      this.segmentationObject.value?.capacity,
    );
  }

  get forecastReferenceChange() {
    return SegmentationObjectValue.calculateChange(
      this.forecastReference,
      this.reference,
    );
  }

  get forecastReferenceRelativeChange() {
    return SegmentationObjectValue.calculateRelativeChange(
      this.forecastReference,
      this.reference,
    );
  }

  get grossBOB() {
    return SegmentationObjectValue.calculateSum(this.BOB, this.cancellationBOB);
  }

  get grossReference() {
    return SegmentationObjectValue.calculateSum(
      this.reference,
      this.cancellationReference,
    );
  }

  get hasOccupancy() {
    return SEGMENTATION_IDS_WITH_OCCUPANCY.includes(
      this.segmentationObject.segmentationId,
    );
  }

  get hasRevpar() {
    return SEGMENTATION_IDS_WITH_REVPAR.includes(
      this.segmentationObject.segmentationId,
    );
  }

  get isTargetActive() {
    return (
      Boolean(this.segmentationObject.budgetValue) ||
      Boolean(this.segmentationObject.forecastValue)
    );
  }

  get pickupLastYear() {
    if (!this.segmentationObject.forecastReference) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    return Object.keys(this.BOB).reduce((pickupLastYear, key) => {
      if (
        key === KPIS.ADR &&
        this.BOB[KPIS.NR_OF_ROOMS] !== null &&
        this.BOB[KPIS.REVENUE] !== null &&
        this.forecastReference[KPIS.NR_OF_ROOMS] !== null &&
        this.forecastReference[KPIS.REVENUE] !== null
      ) {
        const denominator =
          this.forecastReference[KPIS.NR_OF_ROOMS] - this.BOB[KPIS.NR_OF_ROOMS];
        const numerator =
          this.forecastReference[KPIS.REVENUE] - this.BOB[KPIS.REVENUE];

        if (numerator < 0 || denominator <= 0) {
          pickupLastYear[key] = null;
        } else {
          pickupLastYear[key] = numerator / denominator;
        }

        return pickupLastYear;
      }

      if (this.BOB[key] !== null && this.forecastReference[key] !== null) {
        pickupLastYear[key] = this.forecastReference[key] - this.BOB[key];
        return pickupLastYear;
      }

      pickupLastYear[key] = null;
      return pickupLastYear;
    }, {});
  }

  get reference() {
    if (this.isTargetActive) {
      return (
        this.segmentationObject.referenceValue?.copy?.() ?? {
          ...this.segmentationObject?.referenceValue,
        }
      );
    }

    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject.referenceValue,
    );
  }

  get referenceValueDiffReservations() {
    return SegmentationObjectValue.calculateChange(
      this.reference,
      this.reservationsReferenceValue,
    );
  }

  get referenceValueDiffReservationsPercentage() {
    return SegmentationObjectValue.calculateReservationsDiffPercentage(
      this.referenceValueDiffReservations,
      this.reference,
    );
  }

  get relativeChange() {
    return SegmentationObjectValue.calculateRelativeChange(
      this.BOB,
      this.reference,
    );
  }

  get reservationsReferenceValue() {
    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject['referenceValue.reservations'],
      this.segmentationObject.referenceValue?.capacity,
    );
  }

  get reservationsValue() {
    return this.copyAndCalculateOccupancyAndRevpar(
      this.segmentationObject['value.reservations'],
      this.segmentationObject.value?.capacity,
    );
  }

  get valueDiffReservations() {
    return SegmentationObjectValue.calculateChange(
      this.BOB,
      this.reservationsValue,
    );
  }

  get valueDiffReservationsPercentage() {
    return SegmentationObjectValue.calculateReservationsDiffPercentage(
      this.valueDiffReservations,
      this.BOB,
    );
  }

  constructor(segmentationObject) {
    this.segmentationObject = segmentationObject;
  }

  static calculateValues(segmentationObject) {
    return new CalculatedValues(segmentationObject).calculateValues();
  }

  calculateValues() {
    return {
      BOB: this.BOB,
      reference: this.reference,
      change: this.change,
      relativeChange: this.relativeChange,
      budget: this.budget,
      budgetChange: this.budgetChange,
      forecast: this.forecast,
      forecastChange: this.forecastChange,
      forecastReference: this.forecastReference,
      forecastReferenceChange: this.forecastReferenceChange,
      forecastReferenceRelativeChange: this.forecastReferenceRelativeChange,
      pickupLastYear: this.pickupLastYear,
      cancellationBOB: this.cancellationBOB,
      cancellationBOBOrFiltered: this.cancellationBOBOrFiltered,
      cancellationReference: this.cancellationReference,
      cancellationReferenceOrFiltered: this.cancellationReferenceOrFiltered,
      cancellationChange: this.cancellationChange,
      cancellationOrFilteredChange: this.cancellationOrFilteredChange,
      cancellationBOBPercentage: this.cancellationBOBPercentage,
      cancellationBOBOrFilteredPercentage:
        this.cancellationBOBOrFilteredPercentage,
      cancellationReferencePercentage: this.cancellationReferencePercentage,
      cancellationReferenceOrFilteredPercentage:
        this.cancellationReferenceOrFilteredPercentage,
      cancellationChangePercentage: this.cancellationChangePercentage,
      cancellationOrFilteredChangePercentage:
        this.cancellationOrFilteredChangePercentage,
      grossBOB: this.grossBOB,
      grossReference: this.grossReference,
      reservationsValue: this.reservationsValue,
      reservationsReferenceValue: this.reservationsReferenceValue,
      valueDiffReservations: this.valueDiffReservations,
      valueDiffReservationsPercentage: this.valueDiffReservationsPercentage,
      referenceValueDiffReservations: this.referenceValueDiffReservations,
      referenceValueDiffReservationsPercentage:
        this.referenceValueDiffReservationsPercentage,
    };
  }

  copyAndCalculateOccupancyAndRevpar(value, capacity = value?.capacity) {
    if (!value) {
      return SegmentationObjectValue.EMPTY_SEGMENTATION_OBJECT_VALUE;
    }

    const valueCopy = value.copy?.() ?? { ...value };

    if (this.hasOccupancy) {
      SegmentationObjectValue.calculateAndAssignOccupancy(valueCopy, capacity);
    }

    if (this.hasRevpar) {
      SegmentationObjectValue.calculateAndAssignRevpar(valueCopy, capacity);
    }

    return valueCopy;
  }
}
